import React, { useState } from 'react';
import { exportTranscriptsWithEvaluations } from '../api/auth';
import { useNotification } from '../contexts/NotificationContext';

const TranscriptEvaluationExportModal = ({ open, onClose, project }) => {
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [progress, setProgress] = useState(0);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [limit, setLimit] = useState('1000');
  const [splitSize, setSplitSize] = useState('500'); // Default: 500 transcripts per file
  const [includeMessages, setIncludeMessages] = useState(true); // Default: include conversation messages

  if (!open) return null;

  const handleExport = async () => {
    setLoading(true);
    setProgress(10);
    setStatus('Initializing export...');

    try {
      // Step 1: Preparing
      setProgress(20);
      setStatus('Fetching transcripts from database...');
      
      await new Promise(resolve => setTimeout(resolve, 500)); // Brief delay for UX

      // Step 2: Processing
      setProgress(30);
      const willSplit = splitSize !== 'all' && parseInt(limit || '1000', 10) > parseInt(splitSize, 10);
      setStatus(willSplit 
        ? 'Creating Excel file with multiple sheets...'
        : 'Calling Voiceflow Evaluation API... This may take several minutes.'
      );

      // Call the export API with optional filters
      const blob = await exportTranscriptsWithEvaluations(
        project.id,
        startDate || null,
        endDate || null,
        limit || null,
        splitSize || null,
        includeMessages
      );

      // Step 3: Downloaded
      setProgress(90);
      setStatus('Generating Excel file...');

      // Create download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const filename = `transcripts_evaluations_${project.name?.replace(/[^a-z0-9]/gi, '_')}_${Date.now()}.xlsx`;
      link.download = filename;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Complete
      setProgress(100);
      const fileDescription = willSplit 
        ? `Excel file with multiple sheets` 
        : `Excel file`;
      setStatus(`✅ Export completed successfully! Downloaded ${fileDescription}.`);
      
      showSuccess(`Export completed! Downloaded ${filename}`);
      
      // Auto close after success
      setTimeout(() => {
        onClose();
        resetState();
      }, 2000);

    } catch (error) {
      console.error('Export failed:', error);
      setStatus(`❌ Export failed: ${error.message || 'Unknown error'}`);
      showError(`Export failed: ${error.message || 'Please try again'}`);
      setProgress(0);
    } finally {
      setLoading(false);
    }
  };

  const resetState = () => {
    setLoading(false);
    setStatus('');
    setProgress(0);
    setStartDate('');
    setEndDate('');
    setLimit('1000');
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      resetState();
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-8 w-full max-w-2xl border dark:border-gray-800">
        {/* Close button */}
        {!loading && (
          <button
            onClick={handleClose}
            className="absolute right-4 top-4 rounded-full p-2 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        )}

        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#32B1FF] to-blue-600 flex items-center justify-center shadow-lg">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 text-center mb-2">
          Export Transcripts with Evaluations
        </h2>
        
        {/* Project Info */}
        <div className="text-center mb-6">
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Generate a comprehensive Excel report with transcripts and AI evaluations
          </p>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
            </svg>
            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
              Project: {project?.name || 'Unknown'}
            </span>
          </div>
        </div>

        {/* Export Filters */}
        {!loading && !status && (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 mb-6">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
              <svg className="w-4 h-4 text-[#32B1FF]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Export Options (Optional)
            </h3>
            
            <div className="space-y-4">
              {/* Date Range */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-[#32B1FF] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-[#32B1FF] focus:border-transparent"
                  />
                </div>
              </div>

              {/* Limit */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Maximum Transcripts
                  </label>
                  <select
                    value={limit}
                    onChange={(e) => setLimit(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-[#32B1FF] focus:border-transparent"
                  >
                    <option value="100">100 (Fastest)</option>
                    <option value="250">250</option>
                    <option value="500">500</option>
                    <option value="1000">1,000 (Recommended)</option>
                    <option value="2000">2,000</option>
                    <option value="5000">5,000</option>
                    <option value="">All (May be slow)</option>
                  </select>
                </div>
                
                {/* Split Size */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5 flex items-center gap-1">
                    Transcripts per Sheet
                    <span className="group relative">
                      <svg className="w-3.5 h-3.5 text-gray-400 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <span className="invisible group-hover:visible absolute left-0 bottom-full mb-2 w-48 rounded-md px-3 py-2 bg-gray-700 text-white text-xs z-10">
                        Split large exports across multiple sheets in a single Excel file
                      </span>
                    </span>
                  </label>
                  <select
                    value={splitSize}
                    onChange={(e) => setSplitSize(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-[#32B1FF] focus:border-transparent"
                  >
                    <option value="250">250 per sheet</option>
                    <option value="500">500 per sheet (Recommended)</option>
                    <option value="1000">1,000 per sheet</option>
                    <option value="all">All in one sheet</option>
                  </select>
                </div>
              </div>

              {/* Include Messages Toggle */}
              <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={includeMessages}
                      onChange={(e) => setIncludeMessages(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 dark:bg-gray-600 rounded-full peer peer-checked:bg-[#32B1FF] transition-colors"></div>
                    <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5"></div>
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                      Include Conversation Messages
                      <span className="group relative">
                        <svg className="w-3.5 h-3.5 text-gray-400 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <span className="invisible group-hover:visible absolute left-0 bottom-full mb-2 w-56 rounded-md px-3 py-2 bg-gray-700 text-white text-xs z-10">
                          Include full conversation transcripts with individual messages. Disable to export only metadata and evaluations (much faster, smaller file).
                        </span>
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {includeMessages 
                        ? 'Full conversation messages will be included (one row per message)' 
                        : 'Only metadata and evaluations (faster, smaller file)'}
                    </div>
                  </div>
                </label>
              </div>

              <p className="text-xs text-gray-500 dark:text-gray-400 flex items-start gap-1.5">
                <svg className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <span>
                  <strong>Large exports:</strong> For 2,000+ transcripts with detailed conversations, 
                  we recommend splitting across multiple sheets within a single Excel file for easier navigation and performance.
                </span>
              </p>
            </div>
          </div>
        )}


        {/* Progress Section */}
        {loading && (
          <div className="mb-6">
            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Progress
                </span>
                <span className="text-sm font-medium text-[#32B1FF]">
                  {progress}%
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-[#32B1FF] to-blue-600 h-2.5 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {/* Status Message */}
            <div className="flex items-center gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="flex-shrink-0">
                <svg className="animate-spin h-5 w-5 text-[#32B1FF]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-300">{status}</p>
            </div>
          </div>
        )}

        {/* Success/Error Message */}
        {status && !loading && (
          <div className={`mb-6 flex items-start gap-3 p-4 rounded-lg ${
            status.startsWith('✅') 
              ? 'bg-green-50 dark:bg-green-900/20' 
              : 'bg-red-50 dark:bg-red-900/20'
          }`}>
            <div className="flex-shrink-0 mt-0.5">
              {status.startsWith('✅') ? (
                <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <p className={`text-sm ${
              status.startsWith('✅')
                ? 'text-green-700 dark:text-green-300'
                : 'text-red-700 dark:text-red-300'
            }`}>
              {status}
            </p>
          </div>
        )}

        {/* Warning */}
        {!loading && !status && (
          <div className="mb-6 flex items-start gap-3 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
            <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div className="text-sm text-yellow-700 dark:text-yellow-300">
              <p className="font-medium mb-1">Please note:</p>
              <p>This operation may take several minutes depending on the number of transcripts. Do not close this window during the export.</p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 justify-end">
          {!loading && !status && (
            <>
              <button
                onClick={handleClose}
                className="px-6 py-2.5 rounded-lg border dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleExport}
                className="px-6 py-2.5 rounded-lg bg-gradient-to-r from-[#32B1FF] to-blue-600 text-white font-semibold hover:from-[#259ae6] hover:to-blue-700 transition-all shadow-lg shadow-blue-500/25"
              >
                <span className="flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Start Export
                </span>
              </button>
            </>
          )}
          
          {status && !loading && status.startsWith('❌') && (
            <button
              onClick={handleExport}
              className="px-6 py-2.5 rounded-lg bg-[#32B1FF] text-white font-semibold hover:bg-[#259ae6] transition-colors"
            >
              Retry
            </button>
          )}
        </div>

        {/* Help Text */}
        {!loading && !status && (
          <p className="mt-4 text-xs text-center text-gray-500 dark:text-gray-400">
            The exported Excel file will contain two sheets: detailed transcripts with evaluations and a summary sheet.
          </p>
        )}
      </div>
    </div>
  );
};

export default TranscriptEvaluationExportModal;

